import { format, isAfter, isBefore, isToday, isYesterday, set } from 'date-fns';
import { uniq } from 'lodash';
import { NotificationObjectProps, NotificationProps } from '../types';
import { fLinkBuild as buildLink } from '@/utils/formatLink';
import { ROOTS_TASKS } from '@/features/tasks/routes/paths';

// Re-export helper so existing imports keep working while sharing logic.
export const fLinkBuild = buildLink;

const TASK_NOTIFICATION_TABLES = new Set(['task', 'tasks']);
const TASK_ID_KEYS = ['task_id', 'taskId', 'id'];
const TASK_REFERENCE_PATTERN = /^gsc-(\d+)$/i;

export type NotificationCategoryKey =
  | 'jobs'
  | 'payments'
  | 'unassigned'
  | 'reassigned'
  | 'tasks'
  | 'other';

export type NotificationCategoryMeta = {
  key: NotificationCategoryKey;
  label: string;
  icon: string;
  color: string;
};

export const NOTIFICATION_CATEGORY_META: Record<
  NotificationCategoryKey,
  NotificationCategoryMeta
> = {
  jobs: {
    key: 'jobs',
    label: 'Jobs',
    icon: 'eva:checkmark-circle-2-fill',
    color: '#3366FF',
  },
  payments: {
    key: 'payments',
    label: 'Payments',
    icon: 'eva:credit-card-fill',
    color: '#00AB55',
  },
  unassigned: {
    key: 'unassigned',
    label: 'Unassigned',
    icon: 'eva:minus-fill',
    color: '#F59E0B',
  },
  reassigned: {
    key: 'reassigned',
    label: 'Reassigned',
    icon: 'eva:swap-fill',
    color: '#826AF9',
  },
  tasks: {
    key: 'tasks',
    label: 'Tasks',
    icon: 'eva:clipboard-fill',
    color: '#0EA5E9',
  },
  other: {
    key: 'other',
    label: 'Other',
    icon: 'eva:bell-fill',
    color: '#637381',
  },
};

export const NOTIFICATION_CATEGORY_ORDER: NotificationCategoryKey[] = [
  'jobs',
  'payments',
  'unassigned',
  'reassigned',
  'tasks',
  'other',
];

export type RenderableNotificationObjectProps = NotificationObjectProps & {
  notification: NotificationProps & {
    notification_type: NonNullable<NotificationProps['notification_type']>;
  };
};

const normalizeTaskId = (value: unknown) => {
  if (typeof value !== 'number' && typeof value !== 'string') return null;

  const candidate =
    typeof value === 'number'
      ? value
      : Number(value.trim().match(TASK_REFERENCE_PATTERN)?.[1] ?? value);

  if (!Number.isFinite(candidate) || candidate <= 0) return null;

  return String(Math.trunc(candidate));
};

const findTaskIdInRecordMap = (recordMap: Record<string, unknown>) => {
  for (const key of TASK_ID_KEYS) {
    const taskId = normalizeTaskId(recordMap[key]);
    if (taskId) return taskId;
  }

  const values = Object.values(recordMap);
  return values.length === 1 ? normalizeTaskId(values[0]) : null;
};

export const getTaskIdFromNotificationRecordId = (recordId?: unknown) => {
  if (recordId == null) return null;

  if (
    typeof recordId === 'object' &&
    !Array.isArray(recordId) &&
    recordId !== null
  ) {
    return findTaskIdInRecordMap(recordId as Record<string, unknown>);
  }

  const rawRecordId =
    typeof recordId === 'string' || typeof recordId === 'number'
      ? String(recordId).trim()
      : '';
  if (!rawRecordId) return null;

  if (
    (rawRecordId.startsWith('{') && rawRecordId.endsWith('}')) ||
    (rawRecordId.startsWith('[') && rawRecordId.endsWith(']'))
  ) {
    try {
      const parsed = JSON.parse(rawRecordId);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return findTaskIdInRecordMap(parsed);
      }
    } catch (error) {
      console.error('Failed to parse task notification record id JSON', error);
    }
  }

  if (rawRecordId.includes('=')) {
    const searchParams = new URLSearchParams(rawRecordId);

    for (const key of TASK_ID_KEYS) {
      const taskId = normalizeTaskId(searchParams.get(key));
      if (taskId) return taskId;
    }

    const values: string[] = [];
    searchParams.forEach((value) => {
      values.push(value);
    });

    return values.length === 1 ? normalizeTaskId(values[0]) : null;
  }

  return normalizeTaskId(rawRecordId);
};

export const getTaskNotificationPath = ({
  table,
  dashboardRoute,
  recordId,
}: {
  table?: string;
  dashboardRoute?: string;
  recordId?: unknown;
}) => {
  const normalizedTable = table?.trim().toLowerCase() ?? '';
  const normalizedRoute =
    dashboardRoute?.trim().replace(/^\/+/, '').toLowerCase() ?? '';
  const isTaskNotification =
    TASK_NOTIFICATION_TABLES.has(normalizedTable) ||
    normalizedRoute === 'task' ||
    normalizedRoute === 'tasks' ||
    normalizedRoute.startsWith('task/') ||
    normalizedRoute.startsWith('tasks/');

  if (!isTaskNotification) return null;

  const taskId = getTaskIdFromNotificationRecordId(recordId);

  return taskId ? `${ROOTS_TASKS}/${taskId}` : null;
};

export function hasRenderableNotification(
  notificationObject: NotificationObjectProps | null | undefined
): notificationObject is RenderableNotificationObjectProps {
  const entityType =
    notificationObject?.notification?.notification_type?.entity_type;

  return Boolean(notificationObject?.notification && entityType);
}

function notificationMatchesSearch(
  { notification }: RenderableNotificationObjectProps,
  filterName: string
) {
  const searchTerm = filterName.toLowerCase();

  return getNotificationSearchText(notification).includes(searchTerm);
}

export const stripNotificationHtml = (value?: string | null) =>
  (value ?? '')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&pound;/gi, '£')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

const getNotificationContentParts = ({
  title,
  description,
  notification_type,
}: NotificationProps) => {
  const entityType = notification_type?.entity_type;

  return [
    entityType?.table,
    entityType?.dashboard_route,
    title,
    stripNotificationHtml(description),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export const getNotificationCategoryKey = (
  notification: NotificationProps
): NotificationCategoryKey => {
  const content = getNotificationContentParts(notification);
  const table =
    notification.notification_type?.entity_type?.table?.toLowerCase() ?? '';
  const route =
    notification.notification_type?.entity_type?.dashboard_route?.toLowerCase() ??
    '';

  if (
    TASK_NOTIFICATION_TABLES.has(table) ||
    route === 'task' ||
    route === 'tasks' ||
    route.startsWith('task/') ||
    route.startsWith('tasks/')
  ) {
    return 'tasks';
  }

  if (/payment|paid|balance|invoice|transaction|refund|charge/.test(content)) {
    return 'payments';
  }

  if (/unassigned|unassign/.test(content)) {
    return 'unassigned';
  }

  if (/reassigned|reassign/.test(content)) {
    return 'reassigned';
  }

  if (/booking|session|job|cleaner|provider/.test(content)) {
    return 'jobs';
  }

  return 'other';
};

export const getNotificationCategoryMeta = (notification: NotificationProps) =>
  NOTIFICATION_CATEGORY_META[getNotificationCategoryKey(notification)];

export const getNotificationSearchText = (notification: NotificationProps) => {
  const category = getNotificationCategoryMeta(notification);

  return [
    notification.title,
    stripNotificationHtml(notification.description),
    notification.notification_type?.entity_type?.table,
    category.label,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

export const sortNotificationsByCreatedAtDesc = <
  T extends NotificationObjectProps,
>(
  notifications: T[]
) =>
  [...notifications].sort((left, right) => {
    const leftTime = new Date(left.notification.created_at).getTime();
    const rightTime = new Date(right.notification.created_at).getTime();
    const safeLeftTime = Number.isNaN(leftTime) ? 0 : leftTime;
    const safeRightTime = Number.isNaN(rightTime) ? 0 : rightTime;

    if (safeRightTime !== safeLeftTime) return safeRightTime - safeLeftTime;

    return right.id - left.id;
  });

export type NotificationDateGroup<T extends NotificationObjectProps> = {
  key: string;
  label: string;
  notifications: T[];
};

const getNotificationDateGroupMeta = (createdAt: string) => {
  const date = new Date(createdAt);

  if (Number.isNaN(date.getTime())) {
    return { key: 'unknown', label: 'Unknown date' };
  }

  if (isToday(date)) return { key: format(date, 'yyyy-MM-dd'), label: 'Today' };
  if (isYesterday(date)) {
    return { key: format(date, 'yyyy-MM-dd'), label: 'Yesterday' };
  }

  return {
    key: format(date, 'yyyy-MM-dd'),
    label: format(date, 'dd MMM yyyy'),
  };
};

export const groupNotificationsByDate = <T extends NotificationObjectProps>(
  notifications: T[]
): NotificationDateGroup<T>[] => {
  const groups = new Map<string, NotificationDateGroup<T>>();

  notifications.forEach((notificationObject) => {
    const groupMeta = getNotificationDateGroupMeta(
      notificationObject.notification.created_at
    );
    const currentGroup = groups.get(groupMeta.key);

    if (currentGroup) {
      currentGroup.notifications.push(notificationObject);
      return;
    }

    groups.set(groupMeta.key, {
      ...groupMeta,
      notifications: [notificationObject],
    });
  });

  return Array.from(groups.values());
};

export const getNotificationCategoryOptions = (
  notifications: NotificationObjectProps[]
) => {
  const categories = new Set(
    notifications
      .filter(hasRenderableNotification)
      .map(({ notification }) => getNotificationCategoryKey(notification))
  );

  return NOTIFICATION_CATEGORY_ORDER.filter((category) =>
    categories.has(category)
  ).map((category) => NOTIFICATION_CATEGORY_META[category]);
};

export function getLables(notifications: NotificationObjectProps[]) {
  return uniq(
    notifications
      .filter(hasRenderableNotification)
      .map(
        ({ notification }: RenderableNotificationObjectProps) =>
          notification.notification_type.entity_type.table
      )
      .filter(Boolean)
  );
}

export function applySortFilter({
  tableData,
  filterName,
  filterStatus,
  filterTable,
  filterCategory = 'all',
  filterFrom,
  filterTo,
}: {
  tableData: NotificationObjectProps[];
  filterName: string;
  filterStatus: string;
  filterTable: string;
  filterCategory?: NotificationCategoryKey | 'all';
  filterFrom: Date | null;
  filterTo: Date | null;
}) {
  let filteredData = tableData.filter(hasRenderableNotification);

  if (filterName) {
    filteredData = filteredData.filter((item) =>
      notificationMatchesSearch(item, filterName)
    );
  }
  if (filterStatus !== 'all') {
    filteredData = filteredData.filter(
      ({ seen }: NotificationObjectProps) => seen === (filterStatus === 'read')
    );
  }
  if (filterTable !== 'all') {
    filteredData = filteredData.filter(
      ({ notification }: RenderableNotificationObjectProps) =>
        notification.notification_type.entity_type.table === filterTable
    );
  }
  if (filterCategory !== 'all') {
    filteredData = filteredData.filter(
      ({ notification }: RenderableNotificationObjectProps) =>
        getNotificationCategoryKey(notification) === filterCategory
    );
  }
  if (filterFrom) {
    filteredData = filteredData.filter(
      ({ notification: { created_at } }: RenderableNotificationObjectProps) =>
        isAfter(
          new Date(created_at),
          set(filterFrom, { hours: 0, minutes: 0, seconds: 0 })
        )
    );
  }
  if (filterTo) {
    filteredData = filteredData.filter(
      ({ notification: { created_at } }: RenderableNotificationObjectProps) =>
        isBefore(
          new Date(created_at),
          set(filterTo, { hours: 23, minutes: 59, seconds: 59 })
        )
    );
  }

  return sortNotificationsByCreatedAtDesc(filteredData);
}
